import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AmountStep } from "../components/steps/AmountStep";
import { DeptStep } from "../components/steps/DeptStep";
import { MethodStep } from "../components/steps/MethodStep";
import { ProcessingStep } from "../components/steps/ProcessingStep";
import { ReceiptStep } from "../components/steps/ReceiptStep";
import { RefStep } from "../components/steps/RefStep";
import { ResultStep } from "../components/steps/ResultStep";
import type { BootstrapPayload } from "../lib/kiosk-client";

export interface FlowState {
  order_id?: string | undefined;
  payment_id?: string | undefined;
  department_id?: string | undefined;
  external_reference?: string | undefined;
  amount_cents?: number | undefined;
  feature_path?: string | undefined;
  order_status?: string | undefined;
  payment_success?: boolean | undefined;
  error_message?: string | undefined;
}

type FlowStep =
  | "dept"
  | "ref"
  | "amount"
  | "method"
  | "processing"
  | "result"
  | "receipt";

interface Props {
  bootstrap: BootstrapPayload;
  initialState?: Partial<FlowState>;
  onDone: () => void;
}

const FLOW: FlowStep[] = [
  "dept",
  "ref",
  "amount",
  "method",
  "processing",
  "result",
];

export function FlowPage({ bootstrap, initialState, onDone }: Props) {
  const { t } = useTranslation();
  const startStep: FlowStep = initialState?.order_id ? "method" : "dept";
  const [step, setStep] = useState<FlowStep>(startStep);
  const [state, setState] = useState<FlowState>(initialState ?? {});

  function advance(update: Partial<FlowState>): void {
    const next = { ...state, ...update };
    setState(next);
    const currentIndex = FLOW.indexOf(step);
    if (currentIndex < FLOW.length - 1) {
      setStep(FLOW[currentIndex + 1] as FlowStep);
    }
  }

  function goToReceipt(): void {
    setStep("receipt");
  }

  function handleProcessingError(message: string): void {
    setState((s) => ({ ...s, payment_success: false, error_message: message }));
    setStep("result");
  }

  function handleRetry(): void {
    setState((s) => {
      const {
        payment_success: _ps,
        error_message: _em,
        payment_id: _pi,
        ...rest
      } = s;
      return rest;
    });
    setStep("method");
  }

  const canGoBack =
    step !== "dept" &&
    step !== "processing" &&
    step !== "result" &&
    step !== "receipt";

  function goBack(): void {
    const currentIndex = FLOW.indexOf(step);
    if (currentIndex > 0) setStep(FLOW[currentIndex - 1] as FlowStep);
  }

  return (
    <div
      className="flex flex-col h-full bg-[var(--kiosk-brand-bg)]"
      data-testid="flow-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-[var(--kiosk-brand-surface)] border-b border-[var(--kiosk-brand-muted)]/20">
        {canGoBack ? (
          <button
            onClick={goBack}
            className="text-xl font-medium text-[var(--kiosk-brand-accent)] min-h-[44px] px-2"
            aria-label={t("flow.back")}
          >
            ← {t("flow.back")}
          </button>
        ) : (
          <div />
        )}
        <button
          onClick={onDone}
          className="text-xl font-medium text-[var(--kiosk-brand-muted)] min-h-[44px] px-2"
          aria-label={t("flow.cancel")}
        >
          {t("flow.cancel")}
        </button>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-hidden">
        {step === "dept" && <DeptStep state={state} onNext={advance} />}
        {step === "ref" && <RefStep state={state} onNext={advance} />}
        {step === "amount" && <AmountStep state={state} onNext={advance} />}
        {step === "method" && (
          <MethodStep
            state={state}
            methods={bootstrap.payment_methods}
            onNext={advance}
          />
        )}
        {step === "processing" && (
          <ProcessingStep
            state={state}
            onNext={advance}
            onError={handleProcessingError}
            onCancel={onDone}
          />
        )}
        {step === "result" && (
          <ResultStep
            state={state}
            onNext={goToReceipt}
            onRetry={handleRetry}
            onDone={onDone}
          />
        )}
        {step === "receipt" && <ReceiptStep state={state} onDone={onDone} />}
      </div>
    </div>
  );
}
